import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Star } from 'lucide-react';
import type { Application, ApplicationInstance, Environment } from '../types';
import { cn, environmentLabel, primaryInstance } from '../lib/utils';
import { AppIcon } from './AppIcon';
import { HealthDot, NetworkBadge, StatusBadge } from './Badges';

interface ApplicationCardProps {
    application: Application;
    /** Ouvre l'application. Sans `instanceId`, c'est l'instance principale qui part. */
    onOpen: (application: Application, instanceId?: string) => void;
    onToggleFavorite: (application: Application) => void;
}

/** Ordre de présentation : de la production à ce qui en est le plus éloigné. */
const ORDRE_ENVIRONNEMENT: Record<Environment, number> = {
    prod: 0,
    staging: 1,
    dev: 2,
    local: 3,
};

/** Au delà, l'ajout au catalogue ne dit plus rien d'actuel sur l'application. */
const NOUVEAUTE_JOURS = 14;

function estRecente(createdAt: string): boolean {
    const age = Date.now() - new Date(createdAt).getTime();
    return age < NOUVEAUTE_JOURS * 24 * 60 * 60 * 1000;
}

/**
 * Infobulle du bouton d'ouverture.
 *
 * Sur une application accordée qui ne déclare qu'une adresse, elle ne dirait
 * rien que le bouton ne dise déjà. Elle devient utile dès qu'il y a un choix
 * d'adresse, et dès que le droit n'est pas enregistré : il faut alors annoncer
 * que le Hub ouvre la porte sans promettre qu'elle cède.
 */
function infobulleOuverture(
    application: Application,
    principale: ApplicationInstance,
    nombreSecondaires: number
): string | undefined {
    const parts: string[] = [];
    if (!application.hasAccess) {
        parts.push('Accès non enregistré dans le Hub : l’application vérifiera vos droits');
    }
    if (nombreSecondaires > 0) {
        parts.push(destination(principale));
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Les autres adresses de la même application, la plus proche de la production
 * en tête.
 *
 * Le nom court de l'environnement suffit à les distinguer dans tous les cas du
 * catalogue actuel. Deux instances d'un même environnement, deux régions par
 * exemple, rendraient les boutons indiscernables : on reprend alors le libellé
 * déclaré pour l'instance, qui est fait pour ça.
 */
function instancesSecondaires(application: Application, principale: ApplicationInstance) {
    const autres = application.instances
        .filter((i) => i.id !== principale.id)
        .sort((a, b) => ORDRE_ENVIRONNEMENT[a.environment] - ORDRE_ENVIRONNEMENT[b.environment]);

    return autres.map((instance) => ({
        instance,
        libelle: autres.some((i) => i.id !== instance.id && i.environment === instance.environment)
            ? instance.label
            : environmentLabel(instance.environment, true),
    }));
}

/** Ce que dit l'infobulle d'un bouton : où il mène, et sous quelle condition. */
function destination(instance: ApplicationInstance): string {
    const parts = [instance.label, instance.url];
    if (instance.network === 'private') {
        parts.push(
            instance.requiresVpn ? 'Réseau Clippit ou VPN' : 'Réseau interne uniquement'
        );
    } else if (instance.requiresVpn) {
        parts.push('VPN requis');
    }
    return parts.join(' · ');
}

/**
 * Carte d'application du catalogue.
 *
 * Deux questions dominent la lecture d'un catalogue dont une bonne moitié est
 * fermée au lecteur : où se trouve ce que je cherche, et puis-je l'ouvrir. La
 * première revient aux sections de catégorie, la seconde à cette carte, qui y
 * répond par sa forme plutôt que par une pastille : pleine quand l'accès est
 * accordé, tiretée et posée à plat sinon.
 *
 * Les pastilles restantes ne s'affichent que lorsqu'elles apprennent quelque
 * chose. « Actif » est l'état de presque tout le catalogue, et « réseau
 * public » est le cas courant. Restent le statut anormal, le réseau interne
 * ou le VPN, et l'état de santé quand il se dégrade, qui changent ce que le
 * lecteur doit faire.
 *
 * La pastille d'environnement, elle, a disparu. Elle annonçait « Dév » sur le
 * CRA et « Recette » sur TagMSP sans jamais permettre de s'y rendre : elle
 * signalait une porte au lieu de l'ouvrir. Ces environnements sont devenus les
 * boutons qui y mènent, à côté de l'ouverture de la production. L'immense
 * majorité du catalogue n'a qu'une adresse et ne paie rien pour la poignée
 * d'applications qui en ont deux : son pied de carte reste exactement ce qu'il
 * était, un bouton et rien d'autre.
 */
export function ApplicationCard({
    application,
    onOpen,
    onToggleFavorite,
}: ApplicationCardProps) {
    /* L'instance principale est calculée quel que soit le droit. Le Hub
       catalogue, il ne garde pas la porte : une application fermée reste
       ouvrable, à charge pour elle de vérifier les droits à la connexion.
       Seul le ton du bouton change, et il change en CSS. */
    const principale = primaryInstance(application);
    const secondaires = principale ? instancesSecondaires(application, principale) : [];

    const networks = [...new Set(application.instances.map((i) => i.network))];
    const constrainedNetworks = networks.filter(
        (n) => n === 'private' || application.instances.some((i) => i.network === n && i.requiresVpn)
    );

    return (
        <article
            className="app-card"
            data-access={application.hasAccess ? 'granted' : 'missing'}
            style={{ '--app-color': application.color } as CSSProperties}
        >
            <div className="app-card-head">
                <span className="app-card-icon" aria-hidden>
                    <AppIcon name={application.icon} className="w-5 h-5" />
                </span>

                <button
                    type="button"
                    onClick={() => onToggleFavorite(application)}
                    className="app-card-fav"
                    aria-label={
                        application.isFavorite
                            ? `Retirer ${application.name} des favoris`
                            : `Ajouter ${application.name} aux favoris`
                    }
                    aria-pressed={application.isFavorite}
                >
                    <Star
                        className={cn(
                            'w-4 h-4',
                            application.isFavorite &&
                                'fill-[var(--color-warning)] text-[var(--color-warning)]'
                        )}
                    />
                </button>
            </div>

            <Link to={`/applications/${application.slug}`} className="app-card-name">
                {application.name}
            </Link>

            <p className="app-card-desc">{application.shortDescription}</p>

            <div className="app-card-foot">
                {principale && (
                    <>
                        <button
                            type="button"
                            onClick={() => onOpen(application)}
                            className="btn btn-open"
                            title={infobulleOuverture(application, principale, secondaires.length)}
                            aria-label={
                                application.hasAccess
                                    ? undefined
                                    : `Ouvrir ${application.name}, accès non enregistré`
                            }
                        >
                            Ouvrir
                            <ArrowUpRight className="w-4 h-4" aria-hidden />
                        </button>

                        {secondaires.map(({ instance, libelle }) => (
                            <button
                                key={instance.id}
                                type="button"
                                onClick={() => onOpen(application, instance.id)}
                                className="btn-env"
                                title={destination(instance)}
                                aria-label={`Ouvrir ${application.name} : ${instance.label}`}
                            >
                                {libelle}
                            </button>
                        ))}
                    </>
                )}

                {/* Dernier recours : une application qui ne déclare aucune
                    adresse. La fiche est alors tout ce qu'on peut en montrer.
                    L'accès, lui, est binaire (voir migration
                    022_droits_binaires.sql) : il n'y a plus de demande à
                    proposer depuis la carte, un administrateur l'accorde
                    directement ou la synchronisation Entra s'en charge. */}
                {!principale && (
                    <Link to={`/applications/${application.slug}`} className="btn btn-secondary">
                        En savoir plus
                    </Link>
                )}

                {estRecente(application.createdAt) && <span className="badge badge-prod">Nouveau</span>}
                {application.status !== 'active' && <StatusBadge status={application.status} />}
                {principale && (principale.healthStatus === 'degraded' || principale.healthStatus === 'down') && (
                    <HealthDot status={principale.healthStatus} />
                )}
                {constrainedNetworks.map((network) => (
                    <NetworkBadge
                        key={network}
                        network={network}
                        requiresVpn={application.instances.some(
                            (i) => i.network === network && i.requiresVpn
                        )}
                    />
                ))}
            </div>
        </article>
    );
}
